import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { Icon } from '@column/column-ui-kit';
import { DataTable } from '@column/ui';

import { CardTransactionRow, getCardTransactionColumns } from '../Transactions/shared';
import { ROUTE } from '~/app/routes';
import { Loading, Section } from '~/components';
import { Empty as TableEmpty, tableDefaults, useTimeFormat } from '~/components/Table';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardTransactionRepository } from '~/repositories/CardTransactionRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';

import { CardProps } from './Card';

interface Params {
  id: string;
}

export const PageCardTransactions: React.FC = () => {
  const navigate = useNavigate();
  const { id: cardId } = useParams<keyof Params>() as Params;
  useOutletContext<CardProps>(); // Ensures we are nested in card details.
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('card-transactions-by-card');

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [cardId, currentPlatform?.id, isSandbox, resetPagination]);

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['card-transactions', 'by-card', cardId, currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () =>
      CardTransactionRepository.listCardTransactions({
        cardId,
        limit: pageSize,
        startingAfter: cursor,
      }),
    enabled: Boolean(cardId && currentPlatform?.id),
    placeholderData: keepPreviousData,
  });

  const transactions = data?.cardTransactions ?? [];
  const hasMore = Boolean(data?.hasMore);

  return (
    <Inner p={0}>
      <Section headline="Transactions">
        <Loading isLoading={isLoading}>
          <Inner px="var(--page-margin)" py={0}>
            <DataTable<CardTransactionRow>
              {...tableDefaults}
              columns={getCardTransactionColumns(timeFormat, onTimeFormatChange)}
              data={isError ? [] : transactions}
              rowKey={(row) => row.id}
              isLoading={isFetching}
              emptyContent={
                isError ? (
                  <TableEmpty icon={<Icon.Warning />} title="Failed to load card transactions" />
                ) : (
                  <TableEmpty
                    icon={<Icon.AnimationMoney />}
                    title="No transactions yet"
                    description="Transactions will appear here for this card."
                  />
                )
              }
              pagination={{
                hasNext: hasMore,
                hasPrevious,
                pageIndex,
                pageSize,
                onNext: () => {
                  if (transactions.length > 0) {
                    onNext({ cursor: transactions[transactions.length - 1].id });
                  }
                },
                onPrevious,
                onPageSizeChange: setPageSize,
              }}
              onRowClick={(row) => {
                navigate(`${ROUTE.CARD_TRANSACTIONS}/edit/${row.id}`);
              }}
            />
          </Inner>
        </Loading>
      </Section>
    </Inner>
  );
};
